import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useLazyGetClaimAttachmentQuery, useRemoveAdditionalClaimAttachmentMutation } from '../../../data/claim';
import { isRequired } from '../../../utils/validationUtils';
import { VALIDATION_ERROR_MESSAGE } from '../../../constants/errorMessage';

export class CustomUploadDocFile extends File {
  constructor(fileParts, fileName, options, id, path, documentId, documentType, claimFileId) {
    super(fileParts, fileName, options);
    this.id = id;
    this.path = path;
    this.documentId = documentId;
    this.documentType = documentType;
    this.claimFileId = claimFileId;
  }
}

export const useFileUpload = ({ incomingFiles, formKey }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);

  const formContext = useFormContext();
  const [getAttachment] = useLazyGetClaimAttachmentQuery();
  const [removeAdditionalClaimAttachment] = useRemoveAdditionalClaimAttachmentMutation();

  useEffect(() => {
    const fetchFiles = async () => {
      setLoading(true);
      const fetchedFiles = [];

      await Promise.all(
        incomingFiles?.map(async (item) => {
          const path = item?.DocumentPath?.split('/').reverse().join('/');

          try {
            const fileData = await getAttachment(path).unwrap();
            const file = new CustomUploadDocFile(
              [fileData],
              item.FileName,
              { type: item.FileType || 'application/octet-stream' },
              item.DocumentId,
              item.DocumentPath,
              item.DocumentId,
              item.DocumentType,
              item.ClaimFileId
            );
            fetchedFiles.push(file);
          } catch {
            // Error handler
          }
        })
      );

      setFiles(fetchedFiles);
      setLoading(false);
    };

    if (incomingFiles && incomingFiles.length > 0) {
      fetchFiles();
    }
  }, [incomingFiles, getAttachment]);

  useEffect(() => {
    formContext.setValue(formKey, files);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files, formKey, formContext.formState.errors[formKey]]);

  const validationError = () => {
    if (files.length === 0) {
      return isRequired('Please upload at least one item or add a comment below.');
    }
    if (files.length > 8) {
      return VALIDATION_ERROR_MESSAGE.MAX_FILE_COUNT;
    }
    return {};
  };

  const handleRemoveFile = (fileToRemove) => {
    const updatedFiles = fileToRemove.documentId
      ? files.filter((file) => file.documentId !== fileToRemove.documentId)
      : files.filter((file) => file !== fileToRemove);
    formContext.setValue(formKey, updatedFiles);
    formContext.clearErrors(formKey);
    setFiles(updatedFiles);

    if (!fileToRemove.path) {
      return;
    }

    if (fileToRemove.claimFileId) {
      removeAdditionalClaimAttachment({ DocumentPath: fileToRemove.path });
    } else {
      removeAdditionalClaimAttachment({
        DocumentPath: fileToRemove.path,
        DocumentId: fileToRemove.documentId
      });
    }
  };

  const handleFileChange = (event) => {
    const newFiles = event?.target?.files || event?.dataTransfer?.files || [];
    const updatedFiles = [...files, ...Array.from(newFiles)];
    formContext.setValue(formKey, updatedFiles);
    setFiles(updatedFiles);
  };

  return {
    files,
    loading,
    validationError,
    handleRemoveFile,
    handleFileChange,
    formContext
  };
};
